import React, { useEffect, useRef, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

const Container = styled.div`
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  
  /* iOS原生滚动优化 */
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  scroll-behavior: smooth;
  
  /* 确保滚动边界处理 */
  touch-action: pan-y;
  
  /* 隐藏滚动条但保持功能 */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

interface ScrollContainerProps {
  children: React.ReactNode;
  className?: string;
}

// 记录不同路由的滚动位置（内存级，避免跨会话持久化带来的问题）
const scrollPositions = new Map<string, number>();

const ScrollContainer: React.FC<ScrollContainerProps> = ({ children, className }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // 进入页面时恢复该路由的滚动位置，离开时保存
  useLayoutEffect(() => {
    const node = containerRef.current;
    const key = location.pathname;
    if (!node) return;

    const saved = scrollPositions.get(key);
    if (saved !== undefined) {
      node.scrollTo({ top: saved, behavior: 'auto' });
    }

    return () => {
      scrollPositions.set(key, node.scrollTop);
    };
  }, [location.pathname]);

  // 滚动时实时记录当前位置，确保快速切换也能保持最近位置
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // 标记为可滚动区域
    container.setAttribute('data-scrollable', 'true');

    const key = location.pathname;

    const handleScroll = () => {
      scrollPositions.set(key, container.scrollTop);
    };

    // 添加滚动边界控制
    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      container.dataset.startY = touch.clientY.toString();
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      const startY = parseFloat(container.dataset.startY || '0');
      const currentY = touch.clientY;
      const deltaY = currentY - startY;

      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      // 如果在顶部向下滑动，或在底部向上滑动，阻止默认行为
      if ((scrollTop <= 0 && deltaY > 0) || 
          (scrollTop >= scrollHeight - clientHeight && deltaY < 0)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('scroll', handleScroll);
      scrollPositions.set(key, container.scrollTop);
    };
  }, [location.pathname]);

  return (
    <Container ref={containerRef} className={className}>
      {children}
    </Container>
  );
};

export default ScrollContainer;